import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { AlertTriangle, Loader2, ShieldCheck } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { useTranslation } from '../lib/LanguageContext';
import { supabase } from '../lib/supabase';
import { AuthForm } from '../components/AuthForm';
import { forgetOAuthConsent, rememberOAuthConsent } from '../lib/oauthConsentReturn';

// Consent screen of the OAuth flow for the remote MCP server. The `oauth`
// edge function's /authorize endpoint redirects here with the original
// authorization request in the query string; /approve validates it again and
// returns the URL to send the browser back to (with a code or an error).

const APPROVE_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/oauth/approve`;

const REQUEST_PARAMS = [
  'response_type',
  'client_id',
  'redirect_uri',
  'code_challenge',
  'code_challenge_method',
  'state',
] as const;

interface ClientInfo {
  client_name: string | null;
  redirect_host: string;
  redirect_is_loopback: boolean;
}

export function OAuthConsent() {
  const { user, loading: authLoading, signOut } = useAuth();
  const { t } = useTranslation();
  const [client, setClient] = useState<ClientInfo | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const request = useMemo(() => {
    const search = new URLSearchParams(window.location.search);
    const params: Record<string, string> = {};
    for (const key of REQUEST_PARAMS) {
      const value = search.get(key);
      if (value !== null) params[key] = value;
    }
    return params;
  }, []);

  const callApprove = async (action: 'describe' | 'allow' | 'deny', accessToken?: string) => {
    const res = await fetch(APPROVE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify({ ...request, action }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data as Partial<ClientInfo> & { redirect_to?: string };
  };

  useEffect(() => {
    callApprove('describe')
      .then((data) => {
        // An error the client should handle itself (e.g. missing PKCE)
        if (data.redirect_to) {
          window.location.replace(data.redirect_to);
          return;
        }
        setClient(data as ClientInfo);
      })
      .catch((err) => {
        console.error('OAuth consent: invalid request', err);
        setInvalid(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Google sign-in returns to "/", which brings the user back here
  useEffect(() => {
    if (authLoading) return;
    if (user) forgetOAuthConsent();
    else rememberOAuthConsent(window.location.pathname + window.location.search);
  }, [user, authLoading]);

  const decide = async (action: 'allow' | 'deny') => {
    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setSubmitting(false);
        return;
      }
      const data = await callApprove(action, session.access_token);
      if (!data.redirect_to) throw new Error('Missing redirect');
      window.location.assign(data.redirect_to);
    } catch (err) {
      console.error('OAuth consent failed', err);
      setInvalid(true);
      setSubmitting(false);
    }
  };

  const clientName = client?.client_name || t('oauthConsentUnknownClient');

  // Never show the Allow button inside a frame (clickjacking). vercel.json
  // also sends frame-ancestors 'none', but the PWA service worker serves this
  // route from the cached app shell without those headers.
  if (invalid || window.self !== window.top) {
    return (
      <Layout>
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-amber-500 flex-shrink-0" />
          <p className="text-gray-700">{t('oauthConsentInvalidRequest')}</p>
        </div>
      </Layout>
    );
  }

  if (!client || authLoading) {
    return (
      <Layout>
        <div className="flex justify-center py-6">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <div>
        <div className="bg-indigo-600 text-white text-center text-sm px-4 py-3">
          {t('oauthConsentSignInPrompt', { client: clientName })}
        </div>
        <AuthForm />
      </div>
    );
  }

  return (
    <Layout>
      <div className="flex items-center gap-3 mb-5">
        <ShieldCheck className="h-8 w-8 text-indigo-600 flex-shrink-0" />
        <h1 className="text-xl font-bold text-gray-900">
          {t('oauthConsentRequest', { client: clientName })}
        </h1>
      </div>

      <p className="text-gray-700 mb-3">{t('oauthConsentPermissions')}</p>
      <p className="text-sm text-gray-600 mb-3">
        {t('oauthConsentRedirectNotice', { host: client.redirect_host })}
      </p>

      {client.redirect_is_loopback && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-3 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-800">{t('oauthConsentLoopbackWarning')}</p>
        </div>
      )}

      <p className="text-sm text-gray-500 mb-6">{t('oauthConsentRevokeHint')}</p>

      <div className="flex gap-3">
        <button
          onClick={() => decide('deny')}
          disabled={submitting}
          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          {t('oauthConsentDeny')}
        </button>
        <button
          onClick={() => decide('allow')}
          disabled={submitting}
          className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
        >
          {t('oauthConsentAllow')}
        </button>
      </div>

      <div className="mt-6 pt-4 border-t text-sm text-gray-500 flex flex-wrap items-center justify-between gap-2">
        <span className="break-all">{t('oauthConsentSignedInAs', { email: user.email ?? '' })}</span>
        <button
          onClick={() => signOut()}
          disabled={submitting}
          className="text-indigo-600 hover:text-indigo-800"
        >
          {t('oauthConsentSwitchAccount')}
        </button>
      </div>
    </Layout>
  );
}

function Layout({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 sm:p-8 max-w-md w-full">{children}</div>
    </div>
  );
}
